import React, { useEffect, useState } from 'react'
import useScrollToTop from '../../../hooks/useScrollToTop'
import { Grid, GridColumn, GridRow } from 'semantic-ui-react'
import { useDispatch, useSelector } from 'react-redux'
import Message from '../../common/Message'
import generatePreview from '../../../api/generatePreview'
import toEoiApplicationPayload from '../../../state/mapping/toEoiApplicationPayload'
import RegisteredFormField from '../../common/RegisteredFormField'
import Card from '../../common/Card'
import { setField } from '../../../state/slice/form'
import allFieldsValid from '../../../util/allFieldsValid'
import nameFieldsToFullName from '../../../util/nameFieldsToFullName'
import useLoading from '../../../hooks/useLoading'
import Loader from '../../common/Loader'
import FormDownloadLink from '../common/FormDownloadLink'

export const ERROR_GENERATING_PREVIEW = 'We encountered an issue creating your application preview. Please try again later.'
export const FORM_NOT_COMPLETE = 'All portions of the application must be completed ' +
  'before the application becomes available for review.'

export default function Signature () {
  const dispatch = useDispatch()
  const formState = useSelector(state => state.form)
  const { ready, ...verificationFields } = useSelector(state => state.form.segments.verification.fields)

  const { isLoading, loading, done } = useLoading()
  const [canPreview, setCanPreview] = useState()
  const [formPreviews, setFormPreviews] = useState({})
  const [error, setError] = useState()

  useEffect(() => {
    setCanPreview(Object.keys(formState.segments)
      .filter(k => k !== 'verification' && formState.segments[k].enabled)
      .every(k => formState.segments[k].completed))
    // eslint-disable-next-line
  }, [formState, dispatch, setCanPreview])

  useEffect(() => {
    dispatch(setField(
      'segments.verification.fields.ready.value',
      allFieldsValid(verificationFields).toString()
    ))
    dispatch(setField('segments.employee.fields.electronicSignature.value', verificationFields.employeeElectronicSignature.value))

    if (verificationFields.spouseElectronicSignature.pattern) {
      dispatch(setField('segments.dependents.fields.spouse.electronicSignature.value', verificationFields.spouseElectronicSignature.value))
    }
  }, [verificationFields, dispatch])

  useEffect(() => {
    if (canPreview && !isLoading) {
      loading()
      generatePreview(toEoiApplicationPayload(formState))
        .then(r => {
          done()
          setFormPreviews(r)
        })
        .catch(r => {
          done()
          setError(r)
        })
    }
    // eslint-disable-next-line
  }, [canPreview])

  const pluralSuffix = () => {
    return formPreviews.length > 1 ? 's' : ''
  }

  const pageContent = () => {
    /**
     * Initial state for this variable is set to undefined, but almost immediately gets updated in the useEffect hook that sets canPreview.
     * This causes a glitching effect when just checking if canPreview is truthy as it very quickly goes from undefined to true.
     *
     * When canPreview is still undefined, don't do anything.
     * Wait for canPreview to be a boolean value, after it has been set via the determination logic.
     */
    if (canPreview === undefined) {
      return <></>
    }

    if (canPreview) {
      return <>
        <GridRow centered className={`horizontally-divided u-pT6${isLoading ? ' Dimmed' : ''}`}>
          <GridColumn width={16} className='u-mY2'>
            <p>Click the link{pluralSuffix()} below to review the Evidence of Insurability form{pluralSuffix()}. After
              reviewing the form{pluralSuffix()} please check
              the box indicating you, and any proposed insureds, have read and reviewed the
              document{pluralSuffix()}.</p>
          </GridColumn>
          {isLoading && <Loader />}
          {error && <Message
            content={ERROR_GENERATING_PREVIEW}
            type='critical'
          />}
          {(!error) && <>
            <GridColumn width={16} className='u-mY2'>
              {Object.keys(formPreviews).filter(k => formPreviews[k] !== null).map(form => {
                return <FormDownloadLink
                  key={form}
                  formType={form}
                  fileName={formPreviews[form].path}
                />
              })}
            </GridColumn>
            <GridColumn width={16} className='u-mY2'>
              <RegisteredFormField
                id='electronicConsent'
                inputType='checkbox'
                label={<>I and any proposed insured&apos;s acknowledge reading the
                  above Evidence of Insurability form(s) and
                  agreeing to the <a href='/assets/docs/Electronic-Signature-Consent.pdf' target='_blank'>
                    Consent to use of Electronic Signatures and Receipt of Disclosures Exclusively
                    Through Electronic Means</a></>}
                path='segments.verification.fields.agree'
              />
            </GridColumn>
          </>}
        </GridRow>
        <GridRow>
          <GridColumn width={16}>
            <h3 className='u-mB2'>Signature</h3>
          </GridColumn>
        </GridRow>
        <GridRow centered className='horizontally-divided'>
          <GridColumn width={16} className='u-mB2'>
            <p>Any proposed insureds must sign the Evidence of Insurability form by typing the appropriate names in the
              boxes below. An email containing information on what to expect next will be sent
              to: <b>{formState.segments.employee.fields.email.value}</b> after submission of the signatures.
              The final form will appear after submitting the signatures. Please ensure you save a copy for your
              records.</p>
          </GridColumn>
          <GridColumn width={16} className='u-mB2'>
            <Card className='signature-card'>
              <Grid container divided='vertically'>
                <GridRow>
                  <GridColumn textAlign='center' width={3}><h6>Title</h6></GridColumn>
                  <GridColumn textAlign='center' width={4}><h6>Name</h6></GridColumn>
                  <GridColumn textAlign='center' width={5}><h6>Electronic Signature</h6></GridColumn>
                  <GridColumn textAlign='center' width={4}><h6>Date</h6></GridColumn>
                </GridRow>
                {
                  [
                    [
                      'Employee',
                      nameFieldsToFullName(formState, 'segments.employee.fields'),
                      <RegisteredFormField
                        id='employeeElectronicSignature'
                        key='employee-signature'
                        path='segments.verification.fields.employeeElectronicSignature'
                        dirtyOnChange
                      />,
                      <RegisteredFormField
                        id='employeeSignatureDate'
                        disabled={true}
                        key='employee-date'
                        path='segments.verification.fields.employeeSignatureDate'
                      />
                    ],
                    ...(formState.segments.dependents.fields.spouseApplying.value === 'yes' ? [[
                      'Spouse',
                      nameFieldsToFullName(formState, 'segments.dependents.fields.spouse'),
                      <RegisteredFormField
                        id='spouseElectronicSignature'
                        key='spouse-signature'
                        path='segments.verification.fields.spouseElectronicSignature'
                        dirtyOnChange
                      />,
                      <RegisteredFormField
                        id='spouseSignatureDate'
                        disabled={true}
                        key='spouse-date'
                        path='segments.verification.fields.spouseSignatureDate'
                      />
                    ]] : []),
                  ].filter(Boolean).map((item, index) => {
                    return <GridRow key={index}>
                      <GridColumn
                        className='u-pT2'
                        verticalAlign='top'
                        textAlign='center'
                        width={3}>
                        {item[0]}
                      </GridColumn>
                      <GridColumn
                        className='u-pT2'
                        verticalAlign='top'
                        textAlign='center'
                        width={4}>
                        {item[1]}
                      </GridColumn>
                      <GridColumn
                        verticalAlign='top'
                        width={5}>
                        {item[2]}
                      </GridColumn>
                      <GridColumn
                        verticalAlign='top'
                        width={4}>
                        {item[3]}
                      </GridColumn>
                    </GridRow>
                  })
                }
              </Grid>
            </Card>
          </GridColumn>
        </GridRow>
        <GridRow>
          <GridColumn width={16}>
            <p>This web site uses files in Adobe Acrobat PDF format. If you do you have this software or are having
              problems viewing or printing
              these documents, please install the free <a
                href='http://get.adobe.com/reader/'
                rel='noreferrer'
                target='_blank'>Adobe Reader</a></p>
          </GridColumn>
        </GridRow>
      </>
    } else {
      return <GridRow className='horizontally-divided'>
        <GridColumn width={16}>
          <Message content={FORM_NOT_COMPLETE} type='critical' />
        </GridColumn>
      </GridRow>
    }
  }

  return useScrollToTop(<>
    <Grid container stackable>
      <GridRow>
        <GridColumn width={16}>
          <h3 className='u-mB1'>Verify Information</h3>
          <small>To complete submission, the Evidence of Insurability form
            must be reviewed and electronically signed by you.</small>
        </GridColumn>
      </GridRow>
      {pageContent()}
    </Grid>
  </>)
}